import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatIconRegistry } from '@angular/material/icon';
import { MatSnackBar } from '@angular/material/snack-bar';
import { DomSanitizer } from '@angular/platform-browser';
import { ContractCgrpOn } from '@core/models/contract-cgrp-on.model';
import { ContractItem } from '@core/models/contract-item.model';
import { ContractNatioNego } from '@core/models/contract-natio-nego.model';
import { Contract } from '@core/models/contract.model';
import { ContractCgrpOnService } from '@core/services/contract-cgrp-on/contract-cgrp-on.service';
import { merge, Subject } from 'rxjs';
import { finalize, take, takeUntil } from 'rxjs/operators';
import { CGRPOnManagingDialog } from './cgrp-on-managing-dialog/cgrp-on-managing-dialog.component';

@Component({
  standalone: false,
  selector: 'app-contract-cgrp-on',
  templateUrl: './contract-cgrp-on.component.html',
  styleUrls: ['./contract-cgrp-on.component.scss'],
})
export class ContractCgrpOnComponent implements OnInit, OnDestroy {
  @Input() contract: Contract;
  @Input() contractItem: ContractItem;
  @Input() natioNegotiationList: ContractNatioNego[];
  itemsFilterForm: FormGroup;
  componentDestroyed$: Subject<void> = new Subject();
  panelOpened = false;
  isContractOnLoading = false;
  isSavingCgrpOn = false;
  contractOnList: ContractCgrpOn[] = [];
  contractOnCards: { line: ContractCgrpOn; categoryCount: number }[] = [];
  readonly conditionColumns = ['category', 'name', 'number'];
  searchFormControl = new FormControl(null);
  filter = {};

  get isCgrpOnBusy(): boolean {
    return this.isSavingCgrpOn || this.isContractOnLoading;
  }

  constructor(
    private iconRegistry: MatIconRegistry,
    private sanitizer: DomSanitizer,
    public contractCgrpOnService: ContractCgrpOnService,
    private fb: FormBuilder,
    public dialog: MatDialog,
    private snackBar: MatSnackBar
  ) {
    this.itemsFilterForm = this.fb.group({
      startDate: [null],
      endDate: [null],
      search: [null],
    });

    iconRegistry.addSvgIcon('content-copy', sanitizer.bypassSecurityTrustResourceUrl('assets/images/icons/content-copy.svg'));
    iconRegistry.addSvgIcon('edit', sanitizer.bypassSecurityTrustResourceUrl('assets/images/icons/edit.svg'));
    iconRegistry.addSvgIcon('delete-bin', sanitizer.bypassSecurityTrustResourceUrl('assets/images/icons/delete-bin.svg'));
    iconRegistry.addSvgIcon('add-circle-fill', sanitizer.bypassSecurityTrustResourceUrl('assets/images/icons/add-circle-fill.svg'));
    iconRegistry.addSvgIcon('delete-circle-fill', sanitizer.bypassSecurityTrustResourceUrl('assets/images/icons/delete-circle-fill.svg'));
  }

  ngOnInit(): void {
    this.initForm();
    this.getContractOnList();
  }

  ngOnDestroy(): void {
    this.componentDestroyed$.next();
    this.componentDestroyed$.unsubscribe();
  }

  public initForm(): void {
    this.itemsFilterForm.addControl('startDate', this.fb.control(null, [Validators.required]));
    this.itemsFilterForm.addControl('endDate', this.fb.control(null, [Validators.required]));
    this.itemsFilterForm.addControl('search', this.fb.control(null, [Validators.required]));
  }

  public searchContractOnList() {
    this.filter = {
      startDate: this.itemsFilterForm.get('startDate').value ?? '',
      endDate: this.itemsFilterForm.get('endDate').value ?? '',
      name: this.searchFormControl.value ?? '',
    };

    this.getContractOnList(false);
  }

  private getContractOnList(isReload = false) {
    this.isContractOnLoading = true;
    this.filter = Object.assign(this.filter, {
      clientId: this.contract ? this.contract.id : '',
      stateContractYear: this.contract ? this.contract.stateContractYear : '',
    });

    this.contractCgrpOnService
      .getList(this.filter)
      .pipe(
        take(1),
        takeUntil(this.componentDestroyed$),
        finalize(() => {
          this.isContractOnLoading = false;
          if (isReload) this.isSavingCgrpOn = false;
        })
      )
      .subscribe(
        onList => {
          this.contractOnList = onList.items;
          this.sortContractOnList();
        },
        error => {
          this.isContractOnLoading = false;
        }
      );
  }

  public valueChange(): void {
    let todayDate = new Date();

    merge(this.itemsFilterForm.get('startDate').valueChanges, this.itemsFilterForm.get('endDate').valueChanges)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(
        () => {
          // TODO
        },
        (error: Error) => {
          console.error(error);
        }
      );
  }

  public editCGRPOnModal(cgrpOn = null, action = 'new'): void {
    const dialogRef = this.dialog.open(CGRPOnManagingDialog, {
      maxHeight: 'calc(100dvh - 32px)',
      autoFocus: false,
      data: {
        contract: this.contract,
        contractItem: this.contractItem,
        cgrpOnData: cgrpOn,
        action: action,
      },
      panelClass: 'tv-dialog-container',
    });

    dialogRef.componentInstance.saveStarted
      .pipe(takeUntil(dialogRef.afterClosed()), takeUntil(this.componentDestroyed$))
      .subscribe(() => this.isSavingCgrpOn = true);

    dialogRef.afterClosed().pipe(takeUntil(this.componentDestroyed$)).subscribe(rs => {
      if (rs && rs['action'] === 'reload') {
        this.getContractOnList(true);
      }
    });
  }

  public dupCGRPOn(cgrpOn = null): void {
    const dialogRef = this.dialog.open(CGRPOnManagingDialog, {
      maxHeight: 'calc(100dvh - 32px)',
      autoFocus: false,
      data: {
        contract: this.contract,
        contractItem: this.contractItem,
        cgrpOnData: cgrpOn,
        action: 'dup',
      },
      panelClass: 'tv-dialog-container',
    });

    dialogRef.componentInstance.saveStarted
      .pipe(takeUntil(dialogRef.afterClosed()), takeUntil(this.componentDestroyed$))
      .subscribe(() => this.isSavingCgrpOn = true);

    dialogRef.afterClosed().pipe(takeUntil(this.componentDestroyed$)).subscribe(rs => {
      if (rs && rs['action'] === 'reload') {
        this.getContractOnList(true);
      }
    });
  }

  public editCGRPOn(cgrpOn): void {
    const dialogRef = this.dialog.open(CGRPOnManagingDialog, {
      maxHeight: 'calc(100dvh - 32px)',
      autoFocus: false,
      width: '1000px',
      data: {
        contract: this.contract,
        contractItem: this.contractItem,
        cgrpOnData: cgrpOn,
        action: 'edit',
      },
      panelClass: 'tv-dialog-container',
    });

    dialogRef.componentInstance.saveStarted
      .pipe(takeUntil(dialogRef.afterClosed()), takeUntil(this.componentDestroyed$))
      .subscribe(() => this.isSavingCgrpOn = true);

    dialogRef.afterClosed().pipe(takeUntil(this.componentDestroyed$)).subscribe(rs => {
      if (rs && rs['action'] === 'reload') {
        this.getContractOnList(true);
      }
    });
  }

  public confirmDelete(cgrpOn): void {
    const dialogRef = this.dialog.open(CGRPOnManagingDialog, {
      autoFocus: false,
      width: '450px',
      data: {
        contract: this.contract,
        contractItem: this.contractItem,
        cgrpOnData: cgrpOn,
        action: 'delete',
      },
      panelClass: 'tv-dialog-container',
    });

    dialogRef.afterClosed().subscribe(rs => {
      if (rs && rs['action'] === 'delete') {
        this.deleteCgrpOn(cgrpOn);
      }
    });
  }

  // functions for css animations
  onPanelOpened() {
    this.panelOpened = true;
  }

  onPanelClosed() {
    this.panelOpened = false;
  }

  public duplicateItem(item) {
    this.contractCgrpOnService.duplicate(item.id).subscribe(
      rs => {
        this.snackBar.open('La duplication a été validée.', null, {
          duration: 4000,
          verticalPosition: 'top',
          panelClass: ['tv-dialog-container'],
        });
        this.getContractOnList(true);
      },
      error => {
        this.snackBar.open('La duplication a été échouée.', null, {
          duration: 4000,
          verticalPosition: 'top',
          panelClass: ['tv-dialog-container'],
        });
      }
    );
  }

  public deleteCgrpOn(item) {
    this.contractCgrpOnService.delete(item.id).subscribe(
      rs => {
        this.snackBar.open('La suppression a été validée.', null, {
          duration: 4000,
          verticalPosition: 'top',
          panelClass: ['tv-dialog-container'],
        });
        this.getContractOnList(true);
      },
      error => {
        this.snackBar.open('La suppression a été échouée.', null, {
          duration: 4000,
          verticalPosition: 'top',
          panelClass: ['tv-dialog-container'],
        });
      }
    );
  }

  private sortContractOnList() {
    this.contractOnList.forEach(cgrpOnNegotiation => {
      cgrpOnNegotiation.conditions = [...(cgrpOnNegotiation.conditions ?? [])].sort((a, b) => {
        const categoryCompare = (a.categoryType?.name || '').localeCompare(b.categoryType?.name || '', 'fr', {
          sensitivity: 'base',
        });
        return categoryCompare || (a.category?.name || '').localeCompare(b.category?.name || '', 'fr', {
          sensitivity: 'base',
        });
      });
    });
    this.contractOnCards = this.contractOnList.map(line => ({
      line,
      categoryCount: new Set(line.conditions.map(condition => condition.categoryType?.id).filter(id => id != null)).size,
    }));
  }
}
